import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, Stack, Typography} from 'views/components';
import {KeyboardArrowDown, KeyboardArrowRight} from 'views/icons';
import {Translate, translate} from 'views/module';

import FlagGroupRow, {rowGridColumns} from './FlagGroupRow';

import {NOTHING_SCHEDULED_HOURS, OVERDUE_JOB_MINUTES, STUCK_OPEN_MINUTES} from './monitorUtils';

const countBadgeStyles = {
  PENDING: {color: '#4B4B55', backgroundColor: '#F1F1F4'},
  OVERDUE_JOB: {color: '#B77900', backgroundColor: '#FFF7E5'},
  STUCK_OPEN: {color: '#B77900', backgroundColor: '#FFF7E5'},
  NOTHING_SCHEDULED: {color: '#B77900', backgroundColor: '#FFF7E5'},
  MARKED_BAD: {color: '#C4512C', backgroundColor: '#FFF1EC'},
  HUMAN_INTERVENTION: {color: '#4B4B55', backgroundColor: '#F1F1F4'},
  FLAGGED: {color: '#4B4B55', backgroundColor: '#F1F1F4'},
  COMPLETED: {color: '#008A00', backgroundColor: '#E3FAE5'},
};

const getFlagDescription = (flagKey, windowLabel) => {
  return translate(`case.Monitor.Flags.${flagKey}.Description`, {
    minutes: flagKey === 'OVERDUE_JOB' ? OVERDUE_JOB_MINUTES : STUCK_OPEN_MINUTES,
    hours: NOTHING_SCHEDULED_HOURS,
    window: windowLabel,
  });
};

const columnTitleKeys = ['Case', 'Status', 'Detail', 'Participants', 'Created', 'Activity', 'OpenFor'];

const FlagGroups = (props) => {
  const {
    title,
    summaryText,
    windowLabel,
    flagGroups,
    expandedFlagKeys,
    onToggleFlagKey,
    now,
    domainGuidesByRn,
    participantDetails,
  } = props;

  return (
    <Box sx={{borderTop: '1px solid #ECECF1'}}>
      <Stack
        direction={{xs: 'column', md: 'row'}}
        justifyContent='space-between'
        alignItems={{xs: 'flex-start', md: 'center'}}
        spacing={1}
        sx={{px: 3, py: 1.75, borderBottom: '1px solid #ECECF1'}}
      >
        <Stack direction='row' spacing={1.25} alignItems='baseline' flexWrap='wrap' useFlexGap>
          <Typography sx={{fontSize: '13px', fontWeight: 600, fontFamily: 'monospace', color: '#1A1916'}}>
            {title}
          </Typography>
          <Typography sx={{fontSize: '12px', color: '#6D7280'}}>
            {summaryText}
          </Typography>
        </Stack>
        <Typography sx={{fontSize: '11px', color: '#A8A49E'}}>
          <Translate expression='case.Monitor.Detail.Note' />
        </Typography>
      </Stack>
      <Box sx={{overflowX: 'auto'}}>
        {/* Fits the row grid's minimum width plus its padding. */}
        <Box sx={{minWidth: 1200}}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: rowGridColumns,
              columnGap: 2,
              px: 3,
              py: 1,
              borderBottom: '1px solid #ECECF1',
            }}
          >
            {
              columnTitleKeys.map((columnTitleKey) => (
                <Typography
                  key={columnTitleKey}
                  sx={{
                    fontSize: '11px',
                    color: '#A8A49E',
                    textAlign: columnTitleKey === 'OpenFor' ? 'right' : 'left',
                  }}
                >
                  <Translate expression={`case.Monitor.Detail.Columns.${columnTitleKey}`} />
                </Typography>
              ))
            }
          </Box>
          {
            flagGroups.length === 0 &&
            <Typography sx={{fontSize: '13px', color: '#A8A49E', px: 3, py: 2}}>
              <Translate expression='case.Monitor.Detail.Empty' />
            </Typography>
          }
          {
            flagGroups.map((flagGroup) => {
              const isExpanded = expandedFlagKeys.includes(flagGroup.flagKey);

              return (
                <Box key={flagGroup.flagKey}>
                  <ButtonBase
                    onClick={() => onToggleFlagKey(flagGroup.flagKey)}
                    sx={{
                      'display': 'flex',
                      'justifyContent': 'flex-start',
                      'alignItems': 'center',
                      'gap': 1.25,
                      'width': '100%',
                      'px': 3,
                      'py': 1.25,
                      'textAlign': 'left',
                      'borderBottom': '1px solid #F1F1F4',
                      '&:hover': {
                        backgroundColor: '#FAFAFB',
                      },
                    }}
                  >
                    {
                      isExpanded ?
                        <KeyboardArrowDown sx={{fontSize: 18, color: '#6D7280'}} /> :
                        <KeyboardArrowRight sx={{fontSize: 18, color: '#6D7280'}} />
                    }
                    <Typography sx={{fontSize: '13px', fontWeight: 600, color: '#1A1916', whiteSpace: 'nowrap'}}>
                      <Translate expression={`case.Monitor.Flags.${flagGroup.flagKey}.Title`} />
                    </Typography>
                    <Box
                      component='span'
                      sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        lineHeight: '18px',
                        px: 0.75,
                        borderRadius: '4px',
                        ...countBadgeStyles[flagGroup.flagKey],
                      }}
                    >
                      {flagGroup.rows.length}
                    </Box>
                    <Typography sx={{fontSize: '12px', color: '#A8A49E'}}>
                      {getFlagDescription(flagGroup.flagKey, windowLabel)}
                    </Typography>
                  </ButtonBase>
                  {
                    isExpanded &&
                    flagGroup.rows.map((row) => (
                      <FlagGroupRow
                        key={row.caseRn}
                        row={row}
                        now={now}
                        domainGuidesByRn={domainGuidesByRn}
                        participantDetails={participantDetails}
                      />
                    ))
                  }
                </Box>
              );
            })
          }
        </Box>
      </Box>
    </Box>
  );
};

FlagGroups.propTypes = {
  title: PropTypes.string.isRequired,
  summaryText: PropTypes.string.isRequired,
  windowLabel: PropTypes.string.isRequired,
  flagGroups: PropTypes.array.isRequired,
  expandedFlagKeys: PropTypes.array.isRequired,
  onToggleFlagKey: PropTypes.func.isRequired,
  now: PropTypes.number.isRequired,
  domainGuidesByRn: PropTypes.object.isRequired,
  participantDetails: PropTypes.object.isRequired,
};

export default React.memo(FlagGroups);
